// Reading an incident's notes — which of them a person wrote, and which the app
// wrote for them — and the drugs given on incidents.
//
// Reallocating an incident or sharing it writes a note whose wording is the only
// handle there is on it, so the patterns below must stay in step with the backend
// that writes them — the reallocation note in routers/incident.py and share_lead in
// core/incident_shares.py. A change there stops these notes being recognised here,
// and it fails quietly: they simply read as somebody's prose.
//
// Drugs given are not notes: each is a structured record on the incident
// (`drug_administrations`, models/drug_administration.py), served already redacted
// for a reader without Incidents.viewClinical. Older drug notes were converted into
// those records by the 2026-10-17 migration.
//
// Shared because several views need the same reading: the incident dialog, the
// patient-history panel's summary of another incident, and the event summary.

// Reallocating an incident writes the first of these; sharing it writes the second
// (see routers/incident.py, and note_text in core/incident_shares.py). Both record
// who was asked to look at the incident rather than anything about the patient, so
// neither belongs in a summary of it.
//
// Matched as the *whole first line*, and on both ends of the sentence, rather than
// on an opening prefix: "Shared with the family that she had been found." is
// somebody's own note about an incident and must survive, and it opens with exactly
// the same two words. Either line may be followed by more (whether the text went
// out, and to whom it didn't), hence first-line only.
export const COORDINATION_NOTE_PATTERNS = [
  /^Allocation changed from .+ to .+\.$/,
  // Both forms of a share, texted or not — see share_lead in
  // core/incident_shares.py, which is where the wording is decided.
  /^Shared with .+ by SMS\.$/,
  /^Shared with .+, no text sent\.$/,
];

export const isCoordinationNote = (note) => {
  const first = (note?.details || "").trimStart().split("\n")[0].trim();
  return COORDINATION_NOTE_PATTERNS.some(re => re.test(first));
};

// A dose amount without float noise or thousands separators: 1000, 0.3, 2.5.
export const doseAmount = (n) => String(+Number(n).toFixed(6));

// The dose given, as recorded: "1000 mg", or "" on a record made before doses were.
export const doseText = (d) => (d?.dose_quantity != null
  ? `${doseAmount(d.dose_quantity)} ${d.dose_unit || ""}`.trim()
  : "");

// What came off the stock count — not the dose (one inhaler, however many puffs).
export const stockText = (d) => (d.part_unit
  ? "part of one unit"
  : `${d.quantity} from stock`);

// One administration as it reads in a list: the drug, then the dose and route —
// "Paracetamol 500 mg tablets — 1000 mg Oral". A record from before the dose was
// recorded has only the stock figure to go on, so it reads as it always did:
// "Paracetamol 500mg × 2", or "… (part of one unit)" for a tube of cream.
export const drugLine = (d) => {
  const dose = [doseText(d), d.route].filter(Boolean).join(" ");
  if (dose) return `${d.drug_name} — ${dose}`;
  return d.part_unit
    ? `${d.drug_name} (part of one unit)`
    : `${d.drug_name} × ${d.quantity}`;
};

// The administrations that stand — a voided one was recorded in error.
export const standingDrugs = (drugs) => (drugs || []).filter(d => !d.voided_at);

// Every drug given across a set of incidents, as [name, total] pairs ordered by
// total then name — the event summary's list. Part of a unit counts as one, as it
// does in the multi-event comparison (core/incident_drugs.drug_totals).
//
// Note it counts what the reader was served: a reader without Incidents.viewClinical
// is served no drugs at all (core/clinical.py), so the caller must not show this to
// them — an empty list there would read as "no drugs given".
export const drugTotals = (incidents) => {
  const totals = new Map();
  (incidents || []).forEach(i => standingDrugs(i.drug_administrations).forEach(d => {
    totals.set(d.drug_name, (totals.get(d.drug_name) || 0) + (d.quantity || 1));
  }));
  return [...totals.entries()].sort((a, b) => (b[1] - a[1]) || a[0].localeCompare(b[0]));
};

// The notes to show when summarising *another* incident (the patient-history
// panel), oldest first, with the coordination notes dropped. Everything a person
// typed is left exactly as they typed it.
//
// Note this drops the message an edited share was sent with, which can carry the
// clinical ask — it's on the incident itself, one click away in the new tab, which
// is where someone reading it back in full should be.
export const summaryNotes = (notes) => (notes || [])
  .filter(n => !isCoordinationNote(n))
  .sort((a, b) => (a.added_at || "").localeCompare(b.added_at || ""));
